import React, { useEffect, useState } from 'react';
import Table from '../../shared/components/layout/Table';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import Button from '../../shared/components/ui/Button';
import Header from '../../shared/components/layout/Header';
import Search from '../../shared/components/ui/Search';
import { transformData } from '../../super-admin/utils/transformData';
import { useParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import Modal from '../../shared/components/ui/Modal';
import { CreateRateList } from '../components/forms/CreateRateList';
import { CreatePLCRateList } from '../components/forms/CreatePLCRateList';
import { get, set, useForm } from 'react-hook-form';
import EditPricing from '../components/forms/EditPricing';
import Cookies from 'js-cookie';
import {
  getAllPricingByProjectId,
  getSinglePricingById,
  getPlc,
  editPricing
} from '../services/api/implementation/impl';
import toast from 'react-hot-toast';
import FormInput from '../../shared/components/ui/FormInput';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import SecondaryTabbedComponent from '../../shared/components/layout/SecondaryTabbedComponent';
import mixpanel from 'mixpanel-browser';
import { ChevronDown, Edit } from 'lucide-react';
import { createPortal } from 'react-dom';
import editIcon from '../../shared/assets/svg/edit.svg';
import { Plus } from 'lucide-react';
import { useFocusEffect } from 'react-router-dom';

export const RateList = ({ projectName }) => {
  const userId = Cookies.get('userId');
  const { id: projectId } = useParams();
  const { register } = useForm();

  const rawMeasurementUnit = Cookies.get('measurement_type');
  const measurementType =
    !rawMeasurementUnit || rawMeasurementUnit === 'null' || rawMeasurementUnit === 'undefined'
      ? 'sq/ft'
      : rawMeasurementUnit;
  const measurementTypeWithoutSlash = measurementType.replace('/', '');
  const { collapsed } = useSidebar();
  // 1. Local state for selected accommodation type, default = "ALL"
  const [selectedAccommodationType, setSelectedAccommodationType] = useState('ALL');
  const [rateUnit, setRateUnit] = useState(
    rawMeasurementUnit === 'sq/ft' || rawMeasurementUnit === 'sq/mt' ? rawMeasurementUnit : 'sq/ft'
  );
  const [selectedEditType, setSelectedEditType] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unitPricingList, setUnitPricingList] = useState([]);
  const [accommodationPricingList, setAccommodationPricingList] = useState([]);
  const [allPricingList, setAllPricingList] = useState([]);
  const [currentPriceingId, setCurrentPriceingId] = useState(null);

  const [isOpenRateList, setIsOpenRateList] = useState(false);
  const [selectedTableType, setSelectedTableType] = useState('');
  const [isEditingRateList, setIsEditingRateList] = useState(false);

  const [blockDropdownOptions, setBlockDropdownOptions] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState('');
  const [currentBlockWithPLC, setCurrentBlockWithPLC] = useState([]);
  const [selectedBlockName, setSelectedBlockName] = useState('');

  const [isPricingEmpty, setIsPricingEmpty] = useState(false);
  const [isOpenPLCRateList, setIsOpenPLCRateList] = useState(false);
  const [pricingType, setPricingType] = useState('all');

  const [plcMode, setPlcMode] = useState(null);

  // Derive selected block label from options as a reliable fallback
  const selectedBlockLabel = React.useMemo(() => {
    const match = blockDropdownOptions?.find(
      (opt) => String(opt.value) === String(selectedBlockId)
    );
    return match?.label || '';
  }, [blockDropdownOptions, selectedBlockId]);

  // Tab state
  const [activeTab, setActiveTab] = useState('Rate List');

  // Tab configuration
  const tabbedComponentMetaData = {
    tabs: ['Rate List', 'PLC Rates']
  };

  // ------------------ Query Calls ------------------
  const { data: getAllPricingList, refetch: refetchAllPricingList } = useQuery({
    queryKey: ['getAllPricingList', projectId],
    queryFn: () => getAllPricingByProjectId(projectId),
    enabled: !!projectId
  });

  const { data: pricingData, refetch: refetchPricingData } = useQuery({
    queryKey: ['pricingData', currentPriceingId],
    queryFn: () => getSinglePricingById(parseInt(currentPriceingId), projectId),
    enabled: !!currentPriceingId
  });

  const { data: PLCByProjectId, refetch: refetchPLCProject } = useQuery({
    queryKey: ['PLCByProjectId', projectId],
    queryFn: () => getPlc(),
    enabled: !!projectId
  });

  useEffect(() => {
    refetchPricingData();
    refetchAllPricingList();
    refetchPLCProject();
  }, []);

  // ------------------ Effects ------------------

  // Track page view
  useEffect(() => {
    mixpanel.track('Rate List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (getAllPricingList && getAllPricingList.length === 0) {
      setIsPricingEmpty(true);
    } else {
      setIsPricingEmpty(false);
    }
  }, [getAllPricingList]);

  useEffect(() => {
    // If we have no pricing records => 'all'
    // Otherwise, set based on the mode of the first record
    if (isPricingEmpty) {
      setPricingType('all');
    } else if (getAllPricingList && getAllPricingList.length > 0) {
      setPricingType(getAllPricingList[0].mode);
      setSelectedTableType(getAllPricingList[0].mode);
    }
  }, [isPricingEmpty, getAllPricingList, refetchAllPricingList]);

  // **Filter** pricing data by selectedAccommodationType when it changes (or pricing data changes)
  useEffect(() => {
    if (getAllPricingList) {
      const filteredPricingList =
        selectedAccommodationType === 'ALL'
          ? getAllPricingList
          : getAllPricingList.filter((item) => {
              return (
                item.accommodation?.accomodationType === selectedAccommodationType.toUpperCase() ||
                false
              );
            });

      // Separate by mode
      const accommodationData = filteredPricingList.filter((item) => item.mode === 'accommodation');
      const sqftData = filteredPricingList.filter((item) => item.mode === 'sqft');

      setUnitPricingList(
        sqftData.map((el) =>
          transformData(
            handleUnit(
              el,
              selectedAccommodationType === 'ALL'
                ? el.accommodation?.accomodationType
                : selectedAccommodationType
            ),
            unitRateListFormat
          )
        )
      );
      setAccommodationPricingList(
        accommodationData.map((el) =>
          transformData(
            handleAcc(
              el,
              selectedAccommodationType === 'ALL'
                ? el.accommodation?.accomodationType
                : selectedAccommodationType
            ),
            accRatelistFormat
          )
        )
      );
      setAllPricingList(
        filteredPricingList.map((el) =>
          transformData(
            handleUnit(
              el,
              selectedAccommodationType === 'ALL'
                ? el.accommodation?.accomodationType
                : selectedAccommodationType
            ),
            allRateListFormat
          )
        )
      );
    }
  }, [getAllPricingList, selectedAccommodationType]);

  useEffect(() => {
    refetchAllPricingList();
  }, [isOpenRateList]);

  useEffect(() => {
    if (currentPriceingId) {
      refetchPricingData();
    }
  }, [currentPriceingId]);

  // --------------- PLC / Blocks Setup ---------------
  useEffect(() => {
    if (PLCByProjectId && PLCByProjectId.length > 0) {
      const filteredPLCs = PLCByProjectId.filter((plc) =>
        plc.plc_block_mapping.some((block) => block.block.projectId === parseInt(projectId))
      );

      const blocks = filteredPLCs.flatMap((plc) => plc.plc_block_mapping);
      const uniqueBlocks = Array.from(new Set(blocks?.map((block) => block.block_id)))?.map((id) =>
        blocks.find((block) => block.block_id === id)
      );

      const options = uniqueBlocks?.map((item) => ({
        value: item.block.id,
        label: item.block.name
      }));

      // Add "All" option at the beginning
      const optionsWithAll = [{ value: 'all', label: 'All' }, ...options];
      setBlockDropdownOptions(optionsWithAll);

      if (optionsWithAll.length > 0) {
        setSelectedBlockId('all');
      }
    }
  }, [PLCByProjectId, projectId]);

  useEffect(() => {
    if (PLCByProjectId && selectedBlockId) {
      // Handle "All" option
      if (selectedBlockId === 'all') {
        const PLCList = PLCByProjectId.flatMap((plc) => {
          return plc.plc_block_mapping
            .filter((blockMapping) => blockMapping.block.projectId === parseInt(projectId))
            .map((blockMapping) => ({
              id: plc.id,
              name: plc.name,
              plcRate: plc.price || null,
              mode: plc.mode || null,
              blockId: blockMapping.block.id,
              blockName: blockMapping.block.name
            }));
        });

        const uniqueData = Object.values(
          PLCList.reduce((acc, curr) => {
            const key = `${curr.id}-${curr.blockId}`;
            acc[key] = curr;
            return acc;
          }, {})
        );

        const transformedPLCList = uniqueData.map((item) =>
          transformData(item, ['name', 'plcRate', 'id', 'mode', 'blockId', 'blockName'])
        );

        setCurrentBlockWithPLC(transformedPLCList);
        setSelectedBlockName('All Blocks');
      } else {
        // Handle specific block selection
        const filteredBlock = PLCByProjectId.flatMap((plc) =>
          plc.plc_block_mapping.filter((block) => block.block.id === parseInt(selectedBlockId))
        );

        if (filteredBlock.length > 0) {
          const block = filteredBlock[0];
          const blockName = block.block?.name || '';

          const PLCList = PLCByProjectId.flatMap((plc) => {
            return plc.plc_block_mapping
              .filter((blockMapping) => blockMapping.block.id === parseInt(selectedBlockId))
              .map((blockMapping) => ({
                id: plc.id,
                name: plc.name,
                plcRate: plc.price || null,
                mode: plc.mode || null
              }));
          });

          const uniqueData = Object.values(
            PLCList.reduce((acc, curr) => {
              acc[curr.id] = curr;
              return acc;
            }, {})
          );

          const transformedPLCList = uniqueData.map((item) =>
            transformData(item, ['name', 'plcRate', 'id', 'mode'])
          );

          setCurrentBlockWithPLC(transformedPLCList);
          setSelectedBlockName(blockName);
        }
      }
    }
  }, [selectedBlockId, PLCByProjectId, projectId]);

  useEffect(() => {
    if (currentBlockWithPLC.length > 0) {
      const modes = currentBlockWithPLC.map((plc) => plc.mode);
      if (modes.includes('percent')) {
        setPlcMode('percent');
      } else if (modes.includes('numeric')) {
        setPlcMode('numeric');
      }
    }
  }, [currentBlockWithPLC]);

  // ------------------ Handlers ------------------
  const formattedNumberToIndian = (number) => {
    return new Intl.NumberFormat('en-IN').format(number);
  };

  // Handle rate unit conversion
  const convertRate = (value, fromUnit, toUnit) => {
    if (!value || value === '---') return value;

    // Remove currency symbol and commas
    const cleanValue = value.replace(/[₹,]/g, '').trim();
    const numValue = parseFloat(cleanValue);
    if (isNaN(numValue)) return value;

    const normalizeUnit = (unit) => {
      if (!unit) return 'sq/ft';
      const normalized = unit.toLowerCase().replace(/\s+/g, '');
      if (normalized === 'sqft' || normalized === 'sq/ft' || normalized === 'sqfeet')
        return 'sq/ft';
      if (normalized === 'sqmt' || normalized === 'sq/mt' || normalized === 'sqm') return 'sq/mt';
      if (normalized === 'guntha' || normalized === 'gunta') return 'guntha';
      if (normalized === 'cents' || normalized === 'cent') return 'cents';
      return unit;
    };

    const normalizedFromUnit = normalizeUnit(fromUnit);
    const normalizedToUnit = normalizeUnit(toUnit);

    if (normalizedFromUnit === normalizedToUnit) return value;

    // How many sq/ft are in each unit
    const sqFtPerUnit = {
      'sq/ft': 1,
      'sq/mt': 10.764, // 1 sq/mt = 10.764 sq/ft
      guntha: 1089, // 1 guntha = 1089 sq/ft
      cents: 435.6 // 1 cent = 435.6 sq/ft
    };

    // For RATES (price per unit area): conversion is inverted compared to area
    // e.g., ₹5000/sq/ft → ₹53,820/sq/mt (multiply by 10.764)
    // Because 1 sq/mt contains 10.764 sq/ft, so the price per sq/mt is higher
    const ratePerSqFt = numValue / sqFtPerUnit[normalizedFromUnit];
    const convertedValue = ratePerSqFt * sqFtPerUnit[normalizedToUnit];

    return `₹${formattedNumberToIndian(convertedValue.toFixed(2))}`;
  };

  const handleRateUnitChange = (unit) => {
    setRateUnit(unit);

    mixpanel.track('Rate Unit Changed', {
      user_id: userId,
      from_unit: rateUnit,
      to_unit: unit,
      timestamp: new Date().toISOString()
    });
  };

  const handleAcc = (item, selectedAccommodationType) => {
    if (selectedAccommodationType === 'INDEPENDENT') {
      return {
        id: item.id,
        accommodation: item.accommodation.name,
        accommodationType: item.accommodation?.accomodationType || selectedAccommodationType,
        plote_price: `₹${formattedNumberToIndian(item.plote_price)}`,
        construction_price: `₹${formattedNumberToIndian(item.construction_price)}`
      };
    } else {
      return {
        id: item.id,
        accommodation: item.accommodation.name,
        accommodationType: item.accommodation?.accomodationType || selectedAccommodationType,
        price: `₹${formattedNumberToIndian(item.price)}`
      };
    }
  };

  const handleUnit = (item, selectedAccommodationType) => {
    if (selectedAccommodationType === 'INDEPENDENT') {
      return {
        id: item.id,
        accommodation: item.accommodation.name,
        accommodationType: item.accommodation?.accomodationType || selectedAccommodationType,
        plote_price: `₹${formattedNumberToIndian(item.plote_price)}`,
        construction_price: `₹${formattedNumberToIndian(item.construction_price)}`,
        loading_factor: item.loading_factor
      };
    } else {
      return {
        id: item.id,
        accommodation: item.accommodation.name,
        accommodationType: item.accommodation?.accomodationType || selectedAccommodationType,
        price: `₹${formattedNumberToIndian(item.price)}`,
        loading_factor: item.loading_factor
      };
    }
  };

  const unitRateListFormat = [
    'accommodationType',
    'accommodation',
    'price',
    'plote_price',
    'construction_price',
    'loading_factor',
    'actions',
    'id'
  ];
  const accRatelistFormat = [
    'accommodationType',
    'accommodation',
    'price',
    'plote_price',
    'construction_price',
    'loading_factor',
    'actions',
    'id'
  ];
  const allRateListFormat = [
    'accommodationType',
    'accommodation',
    'price',
    'plote_price',
    'construction_price',
    'loading_factor',
    'actions',
    'id'
  ];

  const { mutate: editPricingData } = useMutation({
    mutationFn: (payload) => {
      const formattedPayload = Array.isArray(payload)
        ? payload
        : [
            {
              id: currentPriceingId,
              project_id: parseInt(projectId),
              accommodation_id: pricingData?.accommodation?.id,
              mode: selectedTableType,
              ...(pricingData?.accommodation?.accomodationType === 'INDEPENDENT'
                ? {
                    plote_price: parseInt(payload.plote_price) || parseInt(payload.plot_price) || 0,
                    construction_price: parseInt(payload.construction_price) || 0,
                    loading_factor: parseInt(payload.loading_factor) || 0
                  }
                : {
                    price: parseInt(payload.rate) || parseInt(payload.price) || 0,
                    loading_factor: parseInt(payload.loading_factor) || 0
                  })
            }
          ];

      return editPricing(currentPriceingId, formattedPayload);
    },
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: () => {
      setIsSubmitting(false);
      setCurrentPriceingId(null);
      setIsEditingRateList(false);
      refetchAllPricingList();
      toast.success('Pricing edited successfully');
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error editing pricing', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error editing pricing');
    }
  });

  const pricingEditHandler = (data) => {
    if (Object.prototype.hasOwnProperty.call(data, 'rate')) {
      data.rate = parseInt(data.rate);
    }
    setIsSubmitting(true);
    editPricingData(data);
  };

  const onEditClick = (id) => {
    setCurrentPriceingId(id);
    setIsEditingRateList(true);
  };

  const handleRateList = () => {
    setIsOpenRateList(!isOpenRateList);
  };

  const handleTableTypeChange = (e) => {
    setSelectedTableType(e.target.value);
  };

  const handleBlockNameChange = (e) => {
    setSelectedBlockId(e.target.value);
  };

  const handleAccommodationTypeChange = (e) => {
    setSelectedAccommodationType(e.target.value);
  };

  const onPlCUpdateSuccess = () => {
    setIsOpenPLCRateList(false);
    refetchPLCProject();
  };

  const onCreateSuccess = () => {
    setIsOpenRateList(false);
    refetchAllPricingList(projectId);
  };

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
  };

  let tableHeadersSqFt = [];
  if (selectedAccommodationType === 'ALL') {
    tableHeadersSqFt = [
      'Accommodation Type',
      'Accommodation Name',
      'Plot Price',
      'Construction Price',
      'Loading factor',
      'Action'
    ];
  } else if (selectedAccommodationType === 'INDEPENDENT') {
    tableHeadersSqFt = [
      'Accommodation Type',
      'Accommodation Name',
      'Plot Price',
      'Construction Price',
      'Loading factor',
      'Action'
    ];
  } else {
    tableHeadersSqFt = [
      'Accommodation Type',
      'Accommodation Name',
      'Rates',
      'Loading factor',
      'Action'
    ];
  }

  let tableHeadersAccommodation = [];
  if (selectedAccommodationType === 'ALL') {
    tableHeadersAccommodation = [
      'Accommodation Type',
      'Accommodation Name',
      'Plot Price',
      'Construction Price',
      'Action'
    ];
  } else if (selectedAccommodationType === 'INDEPENDENT') {
    tableHeadersAccommodation = [
      'Accommodation Type',
      'Accommodation Name',
      'Plot Price',
      'Construction Price',
      'Action'
    ];
  } else {
    tableHeadersAccommodation = ['Accommodation Type', 'Accommodation Name', 'Rates', 'Action'];
  }

  // Header dropdown state for in-table filter
  const [openDropdown, setOpenDropdown] = useState(null);
  const accHeaderRef = React.useRef(null);
  const [accDropdownRect, setAccDropdownRect] = useState(null);
  const blockHeaderRef = React.useRef(null);
  const [blockDropdownRect, setBlockDropdownRect] = useState(null);

  const PortalDropdown = ({ anchorRect, children, onClose }) => {
    if (!anchorRect) return null;
    const dropdownRef = React.useRef(null);
    React.useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          onClose && onClose();
        }
      };
      const handleEscape = (event) => {
        if (event.key === 'Escape') {
          onClose && onClose();
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }, [onClose]);
    return createPortal(
      <div
        ref={dropdownRef}
        className="absolute bg-white border rounded shadow z-[1000]"
        style={{
          position: 'fixed',
          left: anchorRect.left,
          top: anchorRect.bottom,
          minWidth: anchorRect.width,
          maxHeight: '250px',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}>
        {children}
      </div>,
      document.body
    );
  };

  const handleHeaderDropdownClick = (type, e) => {
    if (openDropdown === type) {
      setOpenDropdown(null);
      return;
    }
    if (type === 'accommodation' && accHeaderRef?.current) {
      setAccDropdownRect(accHeaderRef?.current?.getBoundingClientRect());
    }
    if (type === 'block' && blockHeaderRef?.current) {
      setBlockDropdownRect(blockHeaderRef?.current?.getBoundingClientRect());
    }
    setOpenDropdown(type);
  };

  const handleSelectAccommodationFilter = (type) => {
    setOpenDropdown(null);
    setSelectedAccommodationType(type);
  };

  return (
    <div className="w-full relative">
      {isOpenRateList && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Create Rate List'}
          onClose={() => setIsOpenRateList(false)}
          width={80}>
          <CreateRateList
            projectName={projectName}
            createSuccess={onCreateSuccess}
            pricingType={pricingType}
            onClose={() => setIsOpenRateList(false)}
          />
        </Modal>
      )}
      {isOpenPLCRateList && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Create PLC Rate List'}
          onClose={() => setIsOpenPLCRateList(false)}
          width={80}>
          <CreatePLCRateList
            projectName={projectName}
            createSuccess={onPlCUpdateSuccess}
            plcList={currentBlockWithPLC}
            blockName={selectedBlockName}
            selectedBlockId={selectedBlockId}
            onClose={() => setIsOpenPLCRateList(false)}
            initialMode={plcMode}
          />
        </Modal>
      )}
      {isEditingRateList && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Edit Rate List'}
          onClose={() => {
            setIsEditingRateList(false);
            setCurrentPriceingId(null);
          }}
          width={80}>
          <EditPricing
            isSubmitting={isSubmitting}
            handleEdit={pricingEditHandler}
            selectedTableType={selectedTableType}
            singlePricingData={pricingData}
            selectedAccommodationType={selectedEditType}
            pricingType={pricingType}
            onClose={() => {
              setIsEditingRateList(false);
            }}
          />
        </Modal>
      )}

      {/* Grid */}
      <GridWrapper width={100}>
        <div className="w-full overflow-clip bg-gray-100">
          {/* Tab Component */}
          <div className="mt-5">
            <SecondaryTabbedComponent
              data={tabbedComponentMetaData}
              activePrimary={activeTab}
              onChangePrimaryTab={handleTabChange}
              rounded={true}></SecondaryTabbedComponent>
          </div>
          {activeTab === 'Rate List' && (
            <div className="fixed bottom-8 right-8 z-50">
              <Button
                displayType="primary"
                onClick={() => {
                  mixpanel.track('Add Rate List Button Clicked', {
                    user_id: userId,
                    timestamp: new Date().toISOString()
                  });
                  handleRateList();
                }}
                customStyle={{
                  borderRadius: '50%',
                  width: '50px',
                  height: '50px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}>
                <Plus size={24} color="white" />
              </Button>
            </div>
          )}
          {activeTab === 'PLC Rates' && (
            <div className="fixed bottom-8 right-8 z-50">
              <Button
                displayType="primary"
                onClick={() => {
                  mixpanel.track('Update PLC Rates Button Clicked', {
                    user_id: userId,
                    timestamp: new Date().toISOString()
                  });
                  setIsOpenPLCRateList(true);
                }}
                customStyle={{
                  borderRadius: '50%',
                  width: '50px',
                  height: '50px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}>
                <Edit size={20} color="white" />
              </Button>
            </div>
          )}
          {/* Rate List Tab Content */}
          {activeTab === 'Rate List' && (
            <>
              <div className="mt-4 flex items-center justify-between">
                <p>
                  Pricing Type:{' '}
                  {measurementTypeWithoutSlash === 'sqmt' && pricingType && pricingType !== 'all'
                    ? 'sqmt'
                    : pricingType && pricingType !== 'all'
                      ? pricingType
                      : ''}
                </p>

                {/* Rate Unit Selector - Only show when pricing type is sqft or sqmt */}
                {(pricingType === 'sqft' || measurementTypeWithoutSlash === 'sqmt') && (
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-700">Rate Unit:</span>
                    <select
                      value={rateUnit}
                      onChange={(e) => handleRateUnitChange(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none bg-white text-gray-900 cursor-pointer">
                      <option value="sq/ft">sq/ft</option>
                      <option value="sq/mt">sq/mt</option>
                      <option value="guntha">guntha</option>
                      <option value="cents">cents</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Tables */}
              {pricingType === 'sqft' && (
                <div className="flex flex-col gap-10 h-[calc(100vh-17.5rem)] overflow-auto">
                  <div className="sqft-table-container">
                    <style>{`
                      .sqft-table-container .table-body { position: static !important; overflow-y: visible !important; max-height: none !important; height: auto !important; top: auto !important; padding-bottom: 0 !important; }
                      .sqft-table-container .table-container { height: auto !important; }
                      .sqft-table-container > div { height: auto !important; }
                    `}</style>

                    <div className=" w-full relative rounded-t-lg mt-3 ">
                      <table className="text-left w-full">
                        <thead className="bg-gray-200 !text-gray-600 w-full sticky top-0 z-20">
                          <tr className="font-normal h-[3rem] w-full">
                            <th
                              className="px-4 py-2 border-b font-normal relative w-[250px]"
                              ref={accHeaderRef}>
                              <span>Accommodation Type</span>
                              <button
                                className="ml-1 align-middle"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleHeaderDropdownClick('accommodation', e);
                                }}>
                                <ChevronDown size={16} />
                              </button>
                              {openDropdown === 'accommodation' && accDropdownRect && (
                                <PortalDropdown
                                  anchorRect={accDropdownRect}
                                  onClose={() => setOpenDropdown(null)}>
                                  {['ALL', 'GROUP', 'INDEPENDENT'].map((opt) => (
                                    <div
                                      key={opt}
                                      className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${selectedAccommodationType === opt && 'bg-blue-100'}`}
                                      onClick={() => handleSelectAccommodationFilter(opt)}>
                                      {opt}
                                    </div>
                                  ))}
                                </PortalDropdown>
                              )}
                            </th>
                            <th className="px-4 py-2 border-b font-normal">Accommodation Name</th>
                            {selectedAccommodationType === 'ALL' ? (
                              <>
                                <th className="px-4 py-2 border-b font-normal">Plot Price</th>
                                <th className="px-4 py-2 border-b font-normal">
                                  Construction Price
                                </th>
                                <th className="px-4 py-2 border-b font-normal">
                                  Rates({rateUnit})
                                </th>
                                <th className="px-4 py-2 border-b font-normal">Loading factor</th>
                              </>
                            ) : selectedAccommodationType === 'INDEPENDENT' ? (
                              <>
                                <th className="px-4 py-2 border-b font-normal">Plot Price</th>
                                <th className="px-4 py-2 border-b font-normal">
                                  Construction Price
                                </th>
                                <th className="px-4 py-2 border-b font-normal">Loading factor</th>
                              </>
                            ) : (
                              <>
                                <th className="px-4 py-2 border-b font-normal">
                                  Rates({rateUnit})
                                </th>
                                <th className="px-4 py-2 border-b font-normal">Loading factor</th>
                              </>
                            )}
                            <th className="px-4 py-2 border-b font-normal">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.isArray(unitPricingList) && unitPricingList.length > 0 ? (
                            unitPricingList.map((item, idx) => (
                              <tr
                                key={item.id || idx}
                                className="border-b hover:bg-gray-50 bg-white h-[3.5rem]">
                                <td className="px-4 py-2 w-[250px]">
                                  {selectedAccommodationType === 'ALL'
                                    ? item.accommodationType
                                    : idx === 0
                                      ? selectedAccommodationType
                                      : ''}
                                </td>
                                <td className="px-4 py-2">{item.accommodation || '---'}</td>
                                {selectedAccommodationType === 'ALL' ? (
                                  <>
                                    <td className="px-4 py-2">{item.plote_price || '---'}</td>
                                    <td className="px-4 py-2">
                                      {item.construction_price || '---'}
                                    </td>
                                    <td className="px-4 py-2">
                                      {convertRate(item.price, measurementType, rateUnit)}
                                    </td>
                                    <td className="px-4 py-2">{item.loading_factor ?? '---'}</td>
                                  </>
                                ) : selectedAccommodationType === 'INDEPENDENT' ? (
                                  <>
                                    <td className="px-4 py-2">{item.plote_price || '---'}</td>
                                    <td className="px-4 py-2">
                                      {item.construction_price || '---'}
                                    </td>
                                    <td className="px-4 py-2">{item.loading_factor ?? '---'}</td>
                                  </>
                                ) : (
                                  <>
                                    <td className="px-4 py-2">
                                      {convertRate(item.price, measurementType, rateUnit)}
                                    </td>
                                    <td className="px-4 py-2">{item.loading_factor ?? '---'}</td>
                                  </>
                                )}
                                <td className="px-4 py-2">
                                  <div className="flex items-center justify-start w-full relative gap-3">
                                    <div
                                      className="cursor-pointer hover:scale-110 transition-all duration-300 action-button"
                                      onClick={(e) => {
                                        setSelectedEditType(item.accommodationType);
                                        e.stopPropagation();
                                        onEditClick(item.id);
                                      }}>
                                      <img src={editIcon} alt="edit__icon" />
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={
                                  selectedAccommodationType === 'ALL'
                                    ? 6
                                    : selectedAccommodationType === 'INDEPENDENT'
                                      ? 6
                                      : 5
                                }
                                className="text-center py-10 align-middle">
                                <div className="flex flex-col items-center justify-center w-full h-full">
                                  <h2 className="text-lg font-bold text-gray-800 mb-1">
                                    No Data Found
                                  </h2>
                                  <p className="text-gray-600 text-sm">
                                    No rates found for the selected accommodation type.
                                  </p>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {(pricingType === 'accommodation' || pricingType !== 'sqft') && (
                <div className="flex flex-col gap-10 h-[calc(100vh-17.5rem)] overflow-auto">
                  <div className="accommodation-table-container">
                    <style>{`
                      .accommodation-table-container .table-body { position: static !important; overflow-y: visible !important; max-height: none !important; height: auto !important; top: auto !important; padding-bottom: 0 !important; }
                      .accommodation-table-container .table-container { height: auto !important; }
                      .accommodation-table-container > div { height: auto !important; }
                    `}</style>

                    <div className=" w-full relative rounded-t-lg mt-3 ">
                      <table className="text-left w-full">
                        <thead className="bg-gray-200 !text-gray-600 w-full sticky top-0 z-20">
                          <tr className="font-normal h-[3rem] w-full">
                            <th
                              className="px-4 py-2 border-b font-normal relative w-[250px]"
                              ref={accHeaderRef}>
                              <span>Accommodation Type</span>
                              <button
                                className="ml-1 align-middle"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleHeaderDropdownClick('accommodation', e);
                                }}>
                                <ChevronDown size={16} />
                              </button>
                              {openDropdown === 'accommodation' && accDropdownRect && (
                                <PortalDropdown
                                  anchorRect={accDropdownRect}
                                  onClose={() => setOpenDropdown(null)}>
                                  {['ALL', 'GROUP', 'INDEPENDENT'].map((opt) => (
                                    <div
                                      key={opt}
                                      className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${selectedAccommodationType === opt && 'bg-blue-100'}`}
                                      onClick={() => handleSelectAccommodationFilter(opt)}>
                                      {opt}
                                    </div>
                                  ))}
                                </PortalDropdown>
                              )}
                            </th>
                            <th className="px-4 py-2 border-b font-normal">Accommodation Name</th>
                            {selectedAccommodationType === 'ALL' ? (
                              <>
                                <th className="px-4 py-2 border-b font-normal">Plot Price</th>
                                <th className="px-4 py-2 border-b font-normal">
                                  Construction Price
                                </th>
                                <th className="px-4 py-2 border-b font-normal">
                                  Rates({rateUnit})
                                </th>
                              </>
                            ) : selectedAccommodationType === 'INDEPENDENT' ? (
                              <>
                                <th className="px-4 py-2 border-b font-normal">Plot Price</th>
                                <th className="px-4 py-2 border-b font-normal">
                                  Construction Price
                                </th>
                              </>
                            ) : (
                              <th className="px-4 py-2 border-b font-normal">Rates({rateUnit})</th>
                            )}
                            <th className="px-4 py-2 border-b font-normal">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.isArray(accommodationPricingList) &&
                          accommodationPricingList.length > 0 ? (
                            accommodationPricingList.map((item, idx) => (
                              <tr
                                key={item.id || idx}
                                className="border-b hover:bg-gray-50 bg-white h-[3.5rem]">
                                <td className="px-4 py-2 w-[250px]">
                                  {selectedAccommodationType === 'ALL'
                                    ? item.accommodationType
                                    : idx === 0
                                      ? selectedAccommodationType
                                      : ''}
                                </td>
                                <td className="px-4 py-2">{item.accommodation || '---'}</td>
                                {selectedAccommodationType === 'ALL' ? (
                                  <>
                                    <td className="px-4 py-2">{item.plote_price || '---'}</td>
                                    <td className="px-4 py-2">
                                      {item.construction_price || '---'}
                                    </td>
                                    <td className="px-4 py-2">{item.price || '---'}</td>
                                  </>
                                ) : selectedAccommodationType === 'INDEPENDENT' ? (
                                  <>
                                    <td className="px-4 py-2">{item.plote_price || '---'}</td>
                                    <td className="px-4 py-2">
                                      {item.construction_price || '---'}
                                    </td>
                                  </>
                                ) : (
                                  <td className="px-4 py-2">{item.price || '---'}</td>
                                )}
                                <td className="px-4 py-2">
                                  <div className="flex items-center justify-start w-full relative gap-3">
                                    <div
                                      className="cursor-pointer hover:scale-110 transition-all duration-300 action-button"
                                      onClick={(e) => {
                                        setSelectedEditType(item.accommodationType);
                                        e.stopPropagation();
                                        onEditClick(item.id);
                                      }}>
                                      <img src={editIcon} alt="edit__icon" />
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={
                                  selectedAccommodationType === 'ALL'
                                    ? 5
                                    : selectedAccommodationType === 'INDEPENDENT'
                                      ? 5
                                      : 4
                                }
                                className="text-center py-10 align-middle">
                                <div className="flex flex-col items-center justify-center w-full h-full">
                                  <h2 className="text-lg font-bold text-gray-800 mb-1">
                                    No Data Found
                                  </h2>
                                  <p className="text-gray-600 text-sm">
                                    No rates found for the selected accommodation type.
                                  </p>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          {/* PLC Rates Tab Content remains unchanged */}
          {activeTab === 'PLC Rates' && (
            <>
              <div className="plc-table-container h-[calc(100vh-14.6rem)] overflow-auto">
                <style>{`
                      .plc-table-container .table-body { position: static !important; overflow-y: visible !important; max-height: none !important; height: auto !important; top: auto !important; padding-bottom: 0 !important; }
                      .plc-table-container .table-container { height: auto !important; }
                      .plc-table-container > div { height: auto !important; }
                    `}</style>
                <div className="overflow-scroll hide-scrollbar w-full relative rounded-t-lg mt-3">
                  <table className="text-left w-full">
                    <thead className="bg-gray-200 !text-gray-600 w-full sticky top-0 z-20">
                      <tr className="font-normal h-[3rem] w-full">
                        <th
                          className="px-4 py-2 border-b font-normal relative w-[250px]"
                          ref={blockHeaderRef}>
                          <span>Block</span>
                          <button
                            className="ml-1 align-middle"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHeaderDropdownClick('block', e);
                            }}>
                            <ChevronDown size={16} />
                          </button>
                          {openDropdown === 'block' && blockDropdownRect && (
                            <PortalDropdown
                              anchorRect={blockDropdownRect}
                              onClose={() => setOpenDropdown(null)}>
                              {blockDropdownOptions?.map((option) => (
                                <div
                                  key={option.value}
                                  className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${String(selectedBlockId) === String(option.value) && 'bg-blue-100'}`}
                                  onClick={() => {
                                    setSelectedBlockId(option.value);
                                    setOpenDropdown(null);
                                  }}>
                                  {option.label}
                                </div>
                              ))}
                            </PortalDropdown>
                          )}
                        </th>
                        <th className="px-4 py-2 border-b font-normal">PLC</th>
                        <th className="px-4 py-2 border-b font-normal">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(currentBlockWithPLC) && currentBlockWithPLC.length > 0 ? (
                        currentBlockWithPLC.map((row, idx) => (
                          <tr
                            key={`${row.id}-${row.blockId || idx}`}
                            className="border-b hover:bg-gray-50 bg-white h-[3.5rem]">
                            <td className="px-4 py-2 w-[250px]">
                              {selectedBlockId === 'all'
                                ? row.blockName || '---'
                                : idx === 0
                                  ? selectedBlockName || selectedBlockLabel
                                  : ''}
                            </td>
                            <td className="px-4 py-2">{row.name || '---'}</td>
                            <td className="px-4 py-2">{row.plcRate ?? '---'}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="text-center py-10 align-middle">
                            <div className="flex flex-col items-center justify-center w-full h-full">
                              <h2 className="text-lg font-bold text-gray-800 mb-1">
                                No Data Found
                              </h2>
                              <p className="text-gray-600 text-sm">
                                No PLC rates found for the selected block.
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </GridWrapper>
    </div>
  );
};
